import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { PenLine, Plus, Trash2, X, Loader2, Eye } from "lucide-react";
import {
  listAdminPosts,
  getAdminPost,
  upsertPost,
  softDeletePost,
} from "@/lib/admin.functions";
import { Prose } from "@/components/public/prose";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { isScheduled } from "@/lib/post-visibility";

const listQ = () => queryOptions({ queryKey: ["admin", "posts"], queryFn: () => listAdminPosts() });

export const Route = createFileRoute("/_authenticated/admin/blog")({
  loader: ({ context }) => context.queryClient.ensureQueryData(listQ()),
  errorComponent: ({ error }) => <p className="p-6 text-muted-foreground">{String(error)}</p>,
  notFoundComponent: RouteNotFound,
  component: BlogAdmin,
});

function BlogAdmin() {
  const { data } = useSuspenseQuery(listQ());
  const [editing, setEditing] = useState<{ mode: "new" } | { mode: "edit"; id: string } | null>(null);
  const [filter, setFilter] = useState<"all" | "draft" | "published" | "scheduled">("all");
  const rows = data.filter((p: any) => {
    if (filter === "all") return true;
    if (filter === "scheduled") return isScheduled(p);
    if (filter === "published") return p.status === "published" && !isScheduled(p);
    return p.status === filter;
  });
  const qc = useQueryClient();
  const del = useServerFn(softDeletePost);

  async function handleDelete(id: string) {
    if (!confirm("متأكدة من حذف هذا المقال؟")) return;
    try {
      await del({ data: { id } });
      toast.success("تم الحذف");
      qc.invalidateQueries({ queryKey: ["admin", "posts"] });
    } catch (e: any) { toast.error(e?.message ?? "فشل الحذف"); }
  }

  return (
    <div>
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="kicker text-modern-orange">إدارة المدوّنة</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-foreground">المقالات</h1>
        </div>
        <div className="flex gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value as any)} className="rounded-full border border-input bg-card px-4 py-2 text-sm">
            <option value="all">الكل</option>
            <option value="draft">مسودات</option>
            <option value="published">منشورة</option>
            <option value="scheduled">مجدولة</option>
          </select>
          <button onClick={() => setEditing({ mode: "new" })} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover">
            <Plus className="h-4 w-4" /> مقال جديد
          </button>
        </div>
      </header>

      <div className="overflow-hidden rounded-2xl bg-card shadow-soft">
        <table className="w-full text-sm">
          <thead className="bg-background/70 text-xs text-muted-foreground">
            <tr>
              <th className="p-4 text-start font-semibold">العنوان</th>
              <th className="p-4 text-start font-semibold">الحالة</th>
              <th className="p-4 text-start font-semibold">القراءات</th>
              <th className="p-4 text-start font-semibold">آخر تعديل</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">لا يوجد مقالات.</td></tr>
            ) : rows.map((p: any) => (
              <tr key={p.id} className="border-t border-border/60 hover:bg-background/40">
                <td className="p-4">
                  <p className="font-display font-bold text-foreground">{p.title_ar}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">/{p.slug}</p>
                </td>
                <td className="p-4">
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${isScheduled(p) ? "bg-accent/25 text-foreground" : p.status === "published" ? "bg-soft-green/30 text-foreground" : "bg-modern-orange/20 text-foreground"}`}>
                    {isScheduled(p) ? "مجدول" : p.status === "published" ? "منشور" : "مسودة"}
                  </span>
                  {isScheduled(p) && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      ينشر في {new Date(p.published_at).toLocaleDateString("en-GB")}
                    </p>
                  )}
                </td>
                <td className="p-4 text-muted-foreground">{p.views_count ?? 0}</td>
                <td className="p-4 text-muted-foreground">{new Date(p.updated_at).toLocaleDateString("ar-u-nu-latn")}</td>
                <td className="p-4 text-end">
                  <div className="inline-flex gap-1">
                    {p.status === "published" && !isScheduled(p) && (
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="rounded-full p-2 text-muted-foreground hover:bg-accent/20 hover:text-foreground" title="معاينة">
                        <Eye className="h-4 w-4" />
                      </a>
                    )}
                    <button onClick={() => setEditing({ mode: "edit", id: p.id })} className="rounded-full p-2 text-muted-foreground hover:bg-accent/20 hover:text-foreground" title="تعديل">
                      <PenLine className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="rounded-full p-2 text-muted-foreground hover:bg-destructive/20 hover:text-destructive" title="حذف">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <PostEditor state={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function PostEditor({ state, onClose }: { state: { mode: "new" } | { mode: "edit"; id: string }; onClose: () => void }) {
  const qc = useQueryClient();
  const save = useServerFn(upsertPost);
  const isEdit = state.mode === "edit";

  const editQ = queryOptions({
    queryKey: ["admin", "post", isEdit ? (state as any).id : "new"],
    queryFn: () => (isEdit ? getAdminPost({ data: { id: (state as any).id } }) : Promise.resolve(null)),
  });
  const { data: initial } = useSuspenseQuery(editQ);

  const [form, setForm] = useState({
    slug: initial?.slug ?? "",
    title_ar: initial?.title_ar ?? "",
    excerpt_ar: initial?.excerpt_ar ?? "",
    seo_title: initial?.seo_title ?? "",
    body_md: initial?.body_md ?? "",
    cover_image: initial?.cover_image ?? "",
    tags: (initial?.tags ?? []).join(", "),
    reading_min: initial?.reading_min ?? 5,
    status: (initial?.status as "draft" | "published") ?? "draft",
    published_at: initial?.published_at ? String(initial.published_at).slice(0, 10) : "",
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  async function submit(status: "draft" | "published") {
    setBusy(true);
    try {
      await save({
        data: {
          id: isEdit ? (state as any).id : undefined,
          slug: form.slug || slugify(form.title_ar),
          title_ar: form.title_ar,
          excerpt_ar: form.excerpt_ar,
          seo_title: form.seo_title.trim() || null,
          body_md: form.body_md,
          cover_image: form.cover_image || null,
          tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
          reading_min: Number(form.reading_min) || 5,
          status,
          published_at: form.published_at ? new Date(`${form.published_at}T09:00:00Z`).toISOString() : null,
        },
      });
      toast.success(status === "published" ? "تم النشر" : "تم الحفظ");
      qc.invalidateQueries({ queryKey: ["admin", "posts"] });
      onClose();
    } catch (e: any) { toast.error(e?.message ?? "فشل الحفظ"); } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-foreground/30 backdrop-blur-sm" />
      <aside onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-3xl overflow-y-auto bg-card p-6 shadow-lift">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold text-foreground">{isEdit ? "تحرير مقال" : "مقال جديد"}</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setPreview(!preview)} className="rounded-full border border-input bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent/15">
              {preview ? "تحرير" : "معاينة"}
            </button>
            <button onClick={onClose} className="rounded-full p-2 hover:bg-accent/20"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {preview ? (
          <div className="mt-6">
            {form.cover_image && <img src={form.cover_image} alt="" className="mb-6 aspect-[16/8] w-full rounded-2xl object-cover" />}
            <h1 className="font-display text-3xl font-extrabold text-foreground">{form.title_ar}</h1>
            {form.excerpt_ar && <p className="mt-3 text-lg text-muted-foreground">{form.excerpt_ar}</p>}
            <div className="mt-6"><Prose md={form.body_md} /></div>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <Field label="العنوان" value={form.title_ar} onChange={(v) => setForm((f) => ({ ...f, title_ar: v, slug: f.slug || slugify(v) }))} />
            <Field label="Slug (URL)" value={form.slug} onChange={(v) => setForm((f) => ({ ...f, slug: v }))} mono />
            <Field label="ملخّص" value={form.excerpt_ar} onChange={(v) => setForm((f) => ({ ...f, excerpt_ar: v }))} textarea rows={2} />
            <Field label="عنوان صفحة البحث (SEO)" value={form.seo_title} onChange={(v) => setForm((f) => ({ ...f, seo_title: v }))} helper="يظهر في نتائج البحث. اتركيه فارغًا ليُستخدم عنوان المقال." />
            <Field label="رابط صورة الغلاف" value={form.cover_image} onChange={(v) => setForm((f) => ({ ...f, cover_image: v }))} mono />
            <div className="grid grid-cols-2 gap-4">
              <Field label="وسوم (مفصولة بفاصلة)" value={form.tags} onChange={(v) => setForm((f) => ({ ...f, tags: v }))} />
              <Field label="تاريخ النشر (يمكن جدولته مستقبلاً)" value={form.published_at} onChange={(v) => setForm((f) => ({ ...f, published_at: v }))} type="date" />
              <Field label="دقائق القراءة" value={String(form.reading_min)} onChange={(v) => setForm((f) => ({ ...f, reading_min: Number(v) || 5 }))} type="number" />
            </div>
            <Field label="المحتوى (Markdown)" value={form.body_md} onChange={(v) => setForm((f) => ({ ...f, body_md: v }))} textarea rows={16} mono />
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-2">
          <button onClick={() => submit("draft")} disabled={busy || !form.title_ar} className="inline-flex items-center gap-2 rounded-full border border-input bg-card px-5 py-2.5 font-display text-sm font-bold text-foreground hover:bg-accent/15 disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} حفظ كمسودة
          </button>
          <button onClick={() => submit("published")} disabled={busy || !form.title_ar} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover disabled:opacity-50">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} نشر
          </button>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, value, onChange, textarea, rows = 3, type = "text", mono, helper }: { label: string; value: string; onChange: (v: string) => void; textarea?: boolean; rows?: number; type?: string; mono?: boolean; helper?: string }) {
  const cls = `w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:border-ring ${mono ? "font-mono text-sm" : "text-base"}`;
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-muted-foreground">{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} className={cls} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
      )}
      {helper && <span className="mt-1 block text-xs text-muted-foreground">{helper}</span>}
    </label>
  );
}

function slugify(s: string) {
  return s.trim().toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "").slice(0, 80) || `post-${Date.now()}`;
}